/*!
=========================================================
* Regiform IMS - AccountSelect Component
=========================================================
* Reusable GL account selection dropdown
* Filters out parent accounts (non-posting accounts)
* Supports filtering by account type
=========================================================
*/

import { useMemo } from 'react';
import { Select, Tag, Space } from 'antd';
import { LockOutlined, WarningOutlined } from '@ant-design/icons';
import { useAccounts, useAccount, useChildAccounts } from '../../../hooks/useAccounts';

/**
 * AccountSelect Component
 * Reusable GL account selector that filters out parent/non-posting accounts.
 *
 * @param {string} accountType - Filter by account type (e.g., "EXPENSE", "ASSET")
 * @param {boolean} isActive - Filter by status (true = ACTIVE only, false = INACTIVE only).
 *   Omitted (default) fetches accounts of every status, preserving existing callers' behavior.
 * @param {Array} accounts - Optionally pass accounts directly instead of fetching
 * @param {boolean} leafOnly - If true, only show leaf (posting) accounts (default: true)
 * @param {boolean} groupByCategory - If true, group options under their account category
 *   (accountCategory.categoryName) instead of a flat list. Use for pickers with a large,
 *   unfiltered account universe (e.g. petty cash — 150+ EXPENSE accounts).
 * @param {string} placeholder - Placeholder text
 * @param {*} value - Selected value
 * @param {Function} onChange - Change handler
 * @param {boolean} disabled - Disable the select
 * @param {boolean} showSearch - Enable search (default: true)
 * @param {boolean} allowClear - Allow clearing selection (default: false)
 * @param {Object} style - Custom styles
 * @param {Array} excludeIds - Account IDs to exclude from the list
 */
const AccountSelect = ({
  accountType,
  isActive,
  accounts: externalAccounts,
  leafOnly = true,
  allowParentSelection = false,
  groupByCategory = false,
  placeholder = 'Select GL account',
  value,
  onChange,
  disabled = false,
  showSearch = true,
  allowClear = false,
  style = { width: '100%' },
  excludeIds = [],
}) => {
  // Fetch accounts if not provided externally. When both isActive and
  // leafOnly are requested, ask the backend for the active-leaf set
  // directly (leaf=true) rather than inferring "has children" client-side
  // from an already status-filtered response — a rollup account whose
  // children are all INACTIVE has no child left in an ACTIVE-only fetch,
  // so the old heuristic never flagged it as a parent and the backend's
  // guard 3 (leaf-only posting) then rejected the "selectable" option
  // (PR #221 review finding 4).
  const fetchParams = {
    ...(accountType ? { accountType } : {}),
    ...(isActive !== undefined ? { isActive } : {}),
    ...(isActive === true && leafOnly ? { leaf: true } : {}),
  };
  const { data: fetchedAccounts = [], isLoading } = useAccounts(fetchParams);

  const accounts = externalAccounts || fetchedAccounts;

  // Detect parent accounts (accounts that have children)
  const parentAccountIds = useMemo(() => {
    const parentIds = new Set();
    accounts.forEach((acc) => {
      if (acc.hasChildren) {
        parentIds.add(acc.id);
      }
      if (acc.parentAccountId != null) {
        parentIds.add(acc.parentAccountId);
      }
    });
    return parentIds;
  }, [accounts]);

  const isParent = (acc) => parentAccountIds.has(acc.id);

  // Accounts surviving the exclude/leaf filters — shared by both the flat
  // and grouped option shapes below.
  const filteredAccounts = useMemo(() => {
    return accounts.filter((acc) => {
      // Exclude specific IDs
      if (excludeIds.includes(acc.id)) return false;
      // If leafOnly, hide parent accounts entirely
      if (leafOnly && isParent(acc)) return false;
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, excludeIds, leafOnly]);

  const toOption = (acc) => ({
    value: acc.id,
    label: (
      <Space size={4} style={{ width: '100%' }}>
        <span>{acc.code} - {acc.name}</span>
        {!leafOnly && isParent(acc) && (
          <Tag color="default" icon={<LockOutlined />} style={{ fontSize: 11, marginLeft: 'auto' }}>
            Parent
          </Tag>
        )}
      </Space>
    ),
    disabled: !leafOnly && !allowParentSelection && isParent(acc),
    searchText: `${acc.code} ${acc.name}`.toLowerCase(),
  });

  // Flat options (default) — one entry per account.
  const flatOptions = useMemo(
    () => filteredAccounts.map(toOption),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredAccounts],
  );

  // Grouped options — one antd option-group per account category, sorted by
  // category name, with accounts inside each group sorted by code. Used for
  // pickers over a large, unfiltered account universe (e.g. petty cash).
  const groupedOptions = useMemo(() => {
    const byCategory = new Map();
    filteredAccounts
      .slice()
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
      .forEach((acc) => {
        const key = acc.categoryName || 'Uncategorized';
        if (!byCategory.has(key)) byCategory.set(key, []);
        byCategory.get(key).push(toOption(acc));
      });
    return Array.from(byCategory.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([categoryName, groupOptions]) => ({
        label: categoryName,
        title: categoryName,
        options: groupOptions,
      }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAccounts]);

  // Fallback for a currently-selected account the fetch+filter above hides
  // entirely — e.g. a legacy DRAFT's backfilled glAccountId that is now
  // INACTIVE, or a rollup account (finding 5, PR #221 review). Without
  // this, antd renders the bare numeric id with no explanation. Only fires
  // when `value` isn't already present among the filtered accounts, so it's
  // a no-op (no extra requests) for the common case.
  const valuePresent = useMemo(
    () => value != null && filteredAccounts.some((acc) => acc.id === value),
    [filteredAccounts, value],
  );
  const missingValueId = value != null && !valuePresent ? value : null;
  const { data: missingAccount } = useAccount(missingValueId);
  const { data: missingAccountChildren } = useChildAccounts(missingValueId);

  const missingOption = useMemo(() => {
    if (!missingAccount) return null;
    const isRollup = (missingAccountChildren?.length || 0) > 0;
    const reasonLabel = !missingAccount.isActive
      ? 'inactive'
      : isRollup
      ? 'rollup — reselect'
      : 'unavailable';
    return {
      value: missingAccount.id,
      label: (
        <Space size={4} style={{ width: '100%' }}>
          <span>{missingAccount.code} - {missingAccount.name}</span>
          <Tag color="warning" icon={<WarningOutlined />} style={{ fontSize: 11, marginLeft: 'auto' }}>
            {reasonLabel}
          </Tag>
        </Space>
      ),
      disabled: true,
      searchText: `${missingAccount.code} ${missingAccount.name}`.toLowerCase(),
    };
  }, [missingAccount, missingAccountChildren]);

  const options = useMemo(() => {
    const base = groupByCategory ? groupedOptions : flatOptions;
    if (!missingOption) return base;
    if (groupByCategory) {
      return [
        { label: 'Currently Selected', title: 'Currently Selected', options: [missingOption] },
        ...base,
      ];
    }
    return [missingOption, ...base];
  }, [groupByCategory, groupedOptions, flatOptions, missingOption]);

  return (
    <Select
      style={style}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      loading={isLoading}
      showSearch={showSearch}
      allowClear={allowClear}
      filterOption={(input, option) =>
        option.searchText?.includes(input.toLowerCase())
      }
      options={options}
      notFoundContent={!options.length ? 'No accounts available' : undefined}
    />
  );
};

export default AccountSelect;
